"use client";

import React from "react";
import { motion } from "framer-motion";
import { IoCheckmark, IoCloseCircle } from "react-icons/io5";

interface StateButtonProps {
  state: "idle" | "submitting" | "success" | "error";
  onClick?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  successText?: string;
  errorText?: string;
  submittingText?: string;
}

export default function StateButton({
  state,
  onClick,
  disabled = false,
  children,
  className = "",
  successText = "Success!",
  errorText = "Error!",
  submittingText = "Submitting...",
}: StateButtonProps) {
  const getButtonContent = () => {
    switch (state) {
      case "submitting":
        return (
          <>
            <motion.div
              className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <span>{submittingText}</span>
          </>
        );
      case "success":
        return (
          <>
            <IoCheckmark size={20} />
            <span>{successText}</span>
          </>
        );
      case "error":
        return (
          <>
            <IoCloseCircle size={20} />
            <span>{errorText}</span>
          </>
        );
      default:
        return children;
    }
  };

  const getButtonStyles = () => {
    const baseStyles = "w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 disabled:cursor-not-allowed cursor-pointer";
    
    switch (state) {
      case "success":
        return `${baseStyles} bg-green-600 text-white ${className}`;
      case "error":
        return `${baseStyles} bg-red-600 text-white ${className}`;
      case "submitting":
        return `${baseStyles} bg-primary text-white ${className}`;
      default:
        return `${baseStyles} bg-primary text-white hover:bg-primary/90 ${className}`;
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || state === "submitting" || state === "success"}
      className={getButtonStyles()}
    >
      {getButtonContent()}
    </button>
  );
} 